"use client";
import React, { useState, useContext } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi"; // Using react-icons for the arrow
import Modal from "../../components/Modal/Modal";
import Cookies from "js-cookie";
import { setToLocalStorage } from "../../helpers/setToLocalStorage";
import { UserContext } from "@/context/ContextProvider";
import ButtonAnimated from "../ButtonAnimated/ButtonAnimated";

import "./CookiesModal.css";

export const CookiesModal = ({ isOpen, setIsOpen }) => {
  const [isEssentialOpen, setIsEssentialOpen] = useState(false);
  const [isAnalyticalOpen, setIsAnalyticalOpen] = useState(false);
  const [isMarketingOpen, setIsMarketingOpen] = useState(false);

  const {
    isEssentialChecked,
    setIsEssentialChecked,
    isAnalyticalChecked,
    setIsAnalyticalChecked,
    isMarketingChecked,
    setIsMarketingChecked,
  } = useContext(UserContext);

  const toggleEssential = () => setIsEssentialOpen(!isEssentialOpen);
  const toggleAnalytical = () => setIsAnalyticalOpen(!isAnalyticalOpen);
  const toggleMarketing = () => setIsMarketingOpen(!isMarketingOpen);

  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const cookieCategories = {
    essential: [
      "usercentrics",
      "firestore",
      "zapier",
      "mailchimp",
      "heroic",
      "fontawesome",
      "gmap_geocoding",
    ],
    analytical: [
      "firebase_analytics",
      "google_analytics",
      "gtm", // ✅ Move GTM here!
      "retarget_online_ads",
      "microsoft_clarity", // 👈 NEW

      "facebook_app_events",
      "meta_pixel",
    ],
    marketing: [
      "google_ads",
      "google_aa",
      "facebook_pixel",
      "google_ads_conversion_tracking",
    ],
  };

  // Function to set cookies as an object value for a specific category
  const setCookiesForCategory = (category) => {
    const cookieValue = JSON.stringify(cookieCategories[category]); // Convert array to JSON string
    Cookies.set(category, cookieValue, { expires: 365 });
  };

  const closeCookieModal = () => {
    setIsOpen(false);
    setToLocalStorage("cookieConsentAccepted", true);
  };

  const submitCookies = () => {
    // Set essential cookies (cannot be unchecked, so always true)
    setCookiesForCategory("essential", isEssentialChecked);

    // Set or remove cookies based on user's choice
    if (isAnalyticalChecked) {
      setCookiesForCategory("analytical");
    } else {
      Cookies.remove("analytical");
    }

    if (isMarketingChecked) {
      setCookiesForCategory("marketing");
    } else {
      Cookies.remove("marketing");
    }

    const consentPreferences = {
      essential: true,
      analytical: isAnalyticalChecked,
      marketing: isMarketingChecked,
    };
    setToLocalStorage("cookieConsentPreferences", consentPreferences);

    // Close the modal after setting/removing cookies
    setIsConfigOpen(false);
    closeCookieModal();
  };

  const submitAllCookies = () => {
    setCookiesForCategory("essential");
    setCookiesForCategory("analytical");
    setCookiesForCategory("marketing");
    setIsAnalyticalChecked(true);

    setIsMarketingChecked(true);
    setIsConfigOpen(false);

    const consentPreferences = {
      essential: true,
      analytical: true,
      marketing: true,
    };
    setToLocalStorage("cookieConsentPreferences", consentPreferences);
    closeCookieModal();
  };

  return (
    <Modal
      isVisible={isOpen}
      onClose={() => {}}
      label="Wir verwenden Cookies"
      placement="center"
      disableCloseOutside
      disableClose
    >
      <div>
        {!isConfigOpen ? (
          <p>
            Mit einem Klick auf &apos;Alles akzeptieren&apos; stimmst du der
            Verwendung von Cookies zu, um dein Nutzungserlebnis zu optimieren.
            Deine Zustimmung gibst du uns damit ausdrücklich. Unter
            &apos;Konfigurieren&apos; hast du die Möglichkeit, deine
            Einstellungen individuell anzupassen (auch nachträglich jederzeit
            auf unserer Datenschutzseite). Weitere Informationen findest du in
            unserer Datenschutzerklärung.
          </p>
        ) : (
          <p>
            Bestimme, welche Cookies du zulassen möchtest. Zusätzliche Cookies
            unterstützen uns dabei, dein Nutzungserlebnis zu optimieren.
          </p>
        )}

        {isConfigOpen && (
          <>
            <div className="CookiesModal-item">
              <div
                className="CookiesModal-Item-wrapper"
                onClick={toggleEssential}
              >
                <div className="CookiesModal-Checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    required
                    disabled
                    checked={isEssentialChecked}
                    onChange={() => setIsEssentialChecked(true)} // Always checked
                    onClick={(e) => e.stopPropagation()} // Prevent the click event from bubbling up
                  />
                  <label>Erforderlich</label>
                </div>
                {isEssentialOpen ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {isEssentialOpen && (
                <div className="CookiesModal-info">
                  Diese Cookies und weiteren Informationen sind unerlässlich für
                  die Funktionsfähigkeit unserer Dienste. Sie gewährleisten die
                  Sicherheit und die einwandfreie Nutzung, wie du es erwartest.
                  Aus diesem Grund können sie nicht deaktiviert werden.
                </div>
              )}
            </div>

            <div className="CookiesModal-item" onClick={toggleAnalytical}>
              <div className="CookiesModal-Item-wrapper">
                <div className="CookiesModal-Checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={isAnalyticalChecked}
                    onClick={(e) => e.stopPropagation()} // Prevent the click event from bubbling up
                    onChange={() =>
                      setIsAnalyticalChecked(!isAnalyticalChecked)
                    }
                  />
                  <label>Analytisch</label>
                </div>
                {isAnalyticalOpen ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {isAnalyticalOpen && (
                <div className="CookiesModal-info">
                  Wir möchten unseren Service für dich so gut wie möglich
                  machen. Daher analysieren wir die Nutzung des Services, um
                  dein Erlebnis zu verbessern.
                </div>
              )}
            </div>

            <div className="CookiesModal-item" onClick={toggleMarketing}>
              <div className="CookiesModal-Item-wrapper">
                <div className="CookiesModal-Checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600"
                    checked={isMarketingChecked}
                    onClick={(e) => e.stopPropagation()} // Prevent the click event from bubbling up
                    onChange={() => setIsMarketingChecked(!isMarketingChecked)}
                  />
                  <label>Marketing</label>
                </div>
                {isMarketingOpen ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {isMarketingOpen && (
                <div className="CookiesModal-info">
                  Wir arbeiten mit ausgewählten Werbepartnern, einschließlich
                  Google, zusammen, um dir personalisierte Werbung auf anderen
                  Webseiten anzuzeigen. Weitere Informationen findest du in
                  unserer{" "}
                  <a
                    href="https://business.safety.google/privacy/"
                    className="link"
                    target="_blank"
                  >
                    Datenschutzerklärung
                  </a>{" "}
                  und in den Datenschutzrichtlinien von Google.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Buttons */}
      <div className="CookiesModal-Button-wrapper">
        <ButtonAnimated
          text="Alle akzeptieren"
          hideChevron
          height="52px"
          fontSize="18px"
          onClick={submitAllCookies}
        />

        {!isConfigOpen ? (
          <span onClick={() => setIsConfigOpen(true)} className="pointer">
            Konfigurieren
          </span>
        ) : (
          <span onClick={submitCookies} className="pointer">
            Auswahl speichern
          </span>
        )}
      </div>
    </Modal>
  );
};
